import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import updatePlatformUser from 'API/PlatformUser/updatePlatformUser';
import { getNotification } from 'API/General/getNotification';
import createUserProfile from 'API/UserProfile/createUserProfile';
import deleteUserProfile from 'API/UserProfile/deleteUserProfile';
import updateUserProfile from 'API/UserProfile/updateUserProfile';
import updatePlatformUserAttributes from 'API/PlatformUser/updatePlatformUserAttributes';
import { toast } from 'react-toastify';
import { PlatformEnums } from '@/utils/platforms';

const useUpdateAllPlatformUsers = ({
    data,
    accountScope,
    oldData,
    currentCompany,
    refetchPlatformUsers,
}) => {
    const { token } = useSelector((state) => state.login);
    return useMutation({
        mutationFn: async () => {
            try {
                let hasChanges = false;

                for (const item of data) {
                    const oldItem = oldData.find((old) => old.id === item.id);

                    if (
                        oldItem &&
                        JSON.stringify(item) === JSON.stringify(oldItem)
                    ) {
                        continue; // Skip if data and oldData are the same
                    }
                    hasChanges = true;

                    // If ONLY attributes have changed, update attributes only, skip the rest
                    const attributeNames = [
                        'platformAttribute1',
                        'platformAttribute2',
                        'platformAttribute3',
                    ];
                    const onlyAttributesChanged =
                        attributeNames.some(
                            (attr) => oldItem?.[attr] !== item?.[attr],
                        ) &&
                        Object.keys(item).every((key) => {
                            if (attributeNames.includes(key)) return true;
                            return (
                                JSON.stringify(oldItem?.[key]) ===
                                JSON.stringify(item?.[key])
                            );
                        });

                    if (onlyAttributesChanged) {
                        const formData = {
                            id: item.id,
                            platformAttribute1: item.platformAttribute1,
                            platformAttribute2: item.platformAttribute2,
                            platformAttribute3: item.platformAttribute3,
                        };
                        await updatePlatformUserAttributes({
                            platformUserId: item.id,
                            formData: formData,
                            scope: accountScope,
                        });
                        continue;
                    }

                    try {
                        // Check if the user profile has changed
                        if (
                            item?.userProfiles[0]?.numberID !==
                            oldItem?.userProfiles[0]?.numberID
                        ) {
                            if (
                                oldItem &&
                                item?.userProfiles[0] ===
                                    oldItem?.userProfiles[0]
                            ) {
                                continue;
                            }

                            // Determining if it's a Teams user profile
                            const userProfile =
                                item?.platformID === PlatformEnums.TEAMS
                                    ? {
                                          ...item?.userProfiles[0],
                                          platformID: item?.platformID,
                                          platformUserID: item?.id,
                                          msDialPlanID:
                                              item?.userProfiles[0]
                                                  ?.msDialPlanID || null,
                                          msVoicePolicyID:
                                              item?.userProfiles[0]
                                                  ?.msVoicePolicyID || null,
                                      }
                                    : {
                                          ...item?.userProfiles[0],
                                          platformID: item?.platformID,
                                          platformUserID: item?.id,
                                      };
                            const userProfileCreate = async () => {
                                if (
                                    item?.userProfiles[0]?.numberID !==
                                    undefined
                                ) {
                                    await createUserProfile({
                                        formData: userProfile,
                                        scope: accountScope,
                                        token: token,
                                    });
                                }
                            };

                            if (
                                item?.managedBy !== oldItem?.managedBy &&
                                item?.managedBy === 0
                            ) {
                                await updatePlatformUser({
                                    formData: item,
                                    scope: accountScope,
                                });
                            }
                            // If the user profile exists, delete it first
                            if (
                                item?.userProfiles[0]?.platformID &&
                                item?.userProfiles[0]?.platformUserID
                            ) {
                                await deleteUserProfile({
                                    numberID:
                                        oldItem?.userProfiles[0]?.numberID,
                                    platformId:
                                        oldItem?.userProfiles[0]?.platformID,
                                    platformUserId:
                                        oldItem?.userProfiles[0]
                                            ?.platformUserID,
                                    scope: accountScope,
                                    token: token,
                                });
                                await userProfileCreate();
                            } else {
                                await userProfileCreate();
                            }
                            // if (item?.platformID === PlatformEnums.TEAMS) {
                            //     await getNotification(
                            //         'company',
                            //         currentCompany,
                            //     );
                            // }
                        }
                        // If the Number ID is the same, just update the platform user
                        else {
                            await updatePlatformUser({
                                formData: item,
                                scope: accountScope,
                            });

                            const userIsManagedByOther =
                                item?.managedBy && item?.managedBy !== 0;

                            // Only update user profile if it exists (user has a number assigned) AND it has changed
                            if (
                                item?.userProfiles &&
                                item?.userProfiles[0] &&
                                !userIsManagedByOther
                            ) {
                                const hasUserProfileChanged =
                                    JSON.stringify(item?.userProfiles[0]) !==
                                    JSON.stringify(oldItem?.userProfiles[0]);
                                if (hasUserProfileChanged) {
                                    await updateUserProfile({
                                        formData: item?.userProfiles[0],
                                        scope: accountScope,
                                    });
                                }
                            }
                            // Trigger sync notification if managedBy changed and user has no number
                            // if (
                            //     item?.managedBy !== oldItem?.managedBy &&
                            //     item?.platformID === PlatformEnums.TEAMS
                            // ) {
                            //     await getNotification(
                            //         'company',
                            //         currentCompany,
                            //     );
                            // }
                        }
                    } catch (error) {
                        const errorMessage = `${error?.response?.data?.message || error}`;
                        toast.error(errorMessage);
                        throw new Error(errorMessage);
                    }
                }

                if (hasChanges) {
                    refetchPlatformUsers();
                    toast.success('All users updated successfully!');
                }
            } catch (error) {
                const errorMessage = `${error}`;
                console.error(errorMessage);
                toast.error(errorMessage);
                throw new Error(errorMessage);
            }
        },
        staleTime: 0,
    });
};

export default useUpdateAllPlatformUsers;
